package com.sddamico.components.widgets

import androidx.compose.runtime.*
import com.sddamico.SitePalette
import com.sddamico.api.ApiException
import com.sddamico.api.apiGet
import com.sddamico.api.apiJson
import com.sddamico.api.apiPost
import com.sddamico.models.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.attributes.selected

@Composable
fun ClaimModal(
    player: PlayerUniverseEntry,
    clubId: String,
    isWaiverClaim: Boolean = false,
    onClose: () -> Unit,
    onSuccess: () -> Unit,
) {
    val scope = rememberCoroutineScope()

    var roster by remember { mutableStateOf<List<RosterPlayerResponse>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var selectedDropId by remember { mutableStateOf("") }
    var submitting by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    // Load roster on open
    LaunchedEffect(Unit) {
        try {
            val leagueYear = apiGet("/api/leagueyears/detail", LeagueYearDetailResponse.serializer())
            val rosterResp = apiGet(
                "/api/clubs/roster?clubId=$clubId&leagueYearId=${leagueYear.id}",
                ClubRosterResponse.serializer()
            )
            roster = rosterResp.players
        } catch (_: Exception) {
            errorMessage = "Failed to load roster"
        }
        loading = false
    }

    // Filter drop candidates: LIV players → LIV_LINE only; non-LIV → active slots only
    val droppablePlayers = roster.filter {
        if (player.isLiv) {
            it.rosterSlot == "LIV_LINE"
        } else {
            it.sport == player.sport && it.rosterSlot != "LIV_LINE"
        }
    }

    // LIV player with empty LIV_LINE slot — no drop needed
    val livLineEmpty = player.isLiv && roster.isNotEmpty() && droppablePlayers.isEmpty()
        && roster.none { it.rosterSlot == "LIV_LINE" }

    // Non-LIV player with an empty active slot for their sport — no drop needed
    val allActiveSlots = when (player.sport) {
        "MENS_GOLF" -> listOf("GOLFER_1", "GOLFER_2", "GOLFER_3", "GOLFER_4", "GOLFER_5")
        "MENS_TENNIS" -> listOf("MENS_TENNIS_1", "MENS_TENNIS_2", "MENS_TENNIS_3", "MENS_TENNIS_4")
        "WOMENS_TENNIS" -> listOf("WOMENS_TENNIS_1", "WOMENS_TENNIS_2")
        else -> emptyList()
    }
    val occupiedSlots = roster.filter { it.sport == player.sport && it.rosterSlot != "LIV_LINE" }.map { it.rosterSlot }.toSet()
    val emptySlotAvailable = !player.isLiv && roster.isNotEmpty() && (allActiveSlots.toSet() - occupiedSlots).isNotEmpty()
    val noDropNeeded = livLineEmpty || emptySlotAvailable

    Modal(onDismiss = onClose) {
        Column(Modifier.fillMaxWidth().gap(1.25.cssRem)) {
            // Header
            Row(Modifier.fillMaxWidth().gap(0.5.cssRem), verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    if (isWaiverClaim) "Waiver Claim: ${player.name}"
                    else if (noDropNeeded) "Add: ${player.name}"
                    else "Add/Drop: ${player.name}",
                    Modifier.fontSize(1.25.cssRem).fontWeight(FontWeight.Bold).color(SitePalette.text)
                )
                Spacer()
                SportBadge(player.sport, player.isLiv)
            }

            if (isWaiverClaim && player.waiverEndDate != null) {
                SpanText(
                    "Waivers process: ${formatWaiverEndDate(player.waiverEndDate)}",
                    Modifier.fontSize(0.8125.cssRem).color(SitePalette.textMuted)
                )
            }

            if (loading) {
                SpanText("Loading roster...", Modifier.color(SitePalette.textMuted).fontSize(0.875.cssRem))
            } else if (noDropNeeded) {
                val slotMessage = if (livLineEmpty) "LIV Line slot"
                    else "empty ${sportDisplayName(player.sport)} slot"
                SpanText(
                    "${player.name} will be added to your $slotMessage",
                    Modifier.color(SitePalette.textSecondary).fontSize(0.875.cssRem)
                )
            } else if (droppablePlayers.isEmpty()) {
                SpanText(
                    "No eligible players to drop in ${sportDisplayName(player.sport)}",
                    Modifier.color(SitePalette.textMuted).fontSize(0.875.cssRem)
                )
            } else {
                // Drop player selector
                Column(Modifier.fillMaxWidth().gap(0.375.cssRem)) {
                    SpanText(
                        "Player to drop",
                        Modifier.fontSize(0.8125.cssRem).fontWeight(FontWeight.SemiBold).color(SitePalette.textSecondary)
                    )
                    Select(attrs = {
                        addEventListener("change") { selectedDropId = it.target.asDynamic().value as? String ?: "" }
                        style {
                            width(100.percent)
                            padding(8.px, 12.px)
                            fontSize(0.875.cssRem)
                            border(1.px, LineStyle.Solid, SitePalette.border)
                            borderRadius(6.px)
                            backgroundColor(SitePalette.background)
                            color(SitePalette.text)
                            property("outline", "none")
                        }
                    }) {
                        Option("", attrs = { if (selectedDropId.isEmpty()) selected() }) { Text("Select a player to drop...") }
                        droppablePlayers.forEach { rp ->
                            Option(rp.id, attrs = { if (selectedDropId == rp.id) selected() }) {
                                Text("${rp.name} (${formatSlotName(rp.rosterSlot)})")
                            }
                        }
                    }
                }
            }

            // Error message
            if (errorMessage != null) {
                Div(
                    Modifier
                        .fillMaxWidth()
                        .backgroundColor(Color("#fef2f2"))
                        .border(1.px, LineStyle.Solid, Color("#fca5a5"))
                        .borderRadius(6.px)
                        .padding(0.75.cssRem)
                        .toAttrs()
                ) {
                    SpanText(errorMessage!!, Modifier.color(Color("#991b1b")).fontSize(0.875.cssRem))
                }
            }

            // Buttons
            Row(Modifier.fillMaxWidth().gap(0.75.cssRem).position(Position.Relative), verticalAlignment = Alignment.CenterVertically) {
                Spacer()

                // Cancel button
                Div(
                    Modifier
                        .backgroundColor(SitePalette.border)
                        .color(SitePalette.text)
                        .borderRadius(8.px)
                        .padding(topBottom = 0.5.cssRem, leftRight = 1.cssRem)
                        .fontSize(0.875.cssRem)
                        .fontWeight(FontWeight.Medium)
                        .cursor(Cursor.Pointer)
                        .onClick { dismiss(onClose) }
                        .toAttrs()
                ) {
                    Text("Cancel")
                }

                // Submit button
                val canSubmit = (selectedDropId.isNotEmpty() || noDropNeeded) && !submitting
                ConfirmableButton(
                    label = if (isWaiverClaim) "Submit Waiver Claim" else "Confirm Add/Drop",
                    loadingLabel = "Submitting...",
                    confirmLabel = "Are you sure?",
                    isLoading = submitting,
                    enabled = canSubmit,
                    buttonModifier = Modifier
                        .backgroundColor(if (canSubmit) SitePalette.primary else SitePalette.border)
                        .color(if (canSubmit) Colors.White else SitePalette.textMuted)
                        .borderRadius(8.px)
                        .padding(topBottom = 0.5.cssRem, leftRight = 1.cssRem)
                        .fontSize(0.875.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .transition(Transition.of("background-color", 150.ms)),
                    onConfirm = {
                        scope.launch {
                            submitting = true
                            errorMessage = null
                            try {
                                val body = apiJson.encodeToString(
                                    SubmitClaimRequest(
                                        addedPlayerId = player.id,
                                        droppedPlayerId = if (noDropNeeded) null else selectedDropId,
                                    )
                                )
                                apiPost("/api/claims/submit", body, PlayerClaimResponse.serializer())
                                dismiss(onSuccess)
                            } catch (e: ApiException) {
                                errorMessage = e.errorMessage
                            } catch (_: Exception) {
                                errorMessage = "Failed to submit claim"
                            }
                            submitting = false
                        }
                    },
                )
            }
        }
    }
}

private fun sportDisplayName(sport: String): String = when (sport) {
    "MENS_GOLF" -> "Golf"
    "MENS_TENNIS" -> "Men's Tennis"
    "WOMENS_TENNIS" -> "Women's Tennis"
    else -> sport
}

private fun formatSlotName(slot: String): String = when (slot) {
    "GOLFER_1" -> "Golfer 1"
    "GOLFER_2" -> "Golfer 2"
    "GOLFER_3" -> "Golfer 3"
    "GOLFER_4" -> "Golfer 4"
    "GOLFER_5" -> "Golfer 5"
    "LIV_LINE" -> "LIV Line"
    "MENS_TENNIS_1" -> "Men's Tennis 1"
    "MENS_TENNIS_2" -> "Men's Tennis 2"
    "MENS_TENNIS_3" -> "Men's Tennis 3"
    "MENS_TENNIS_4" -> "Men's Tennis 4"
    "WOMENS_TENNIS_1" -> "Women's Tennis 1"
    "WOMENS_TENNIS_2" -> "Women's Tennis 2"
    else -> slot
}
